import { useState } from 'react'
import { Button } from '@/common/ui/button'
import { Separator } from '@/common/ui/separator'
import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { DataTableSimpleSearch } from '@/common/ui/table/data-table-simple-search'
import { Plus, RefreshCw } from 'lucide-react'
import Heading from '@/common/shared/heading'
import { PlayerTagResponse } from '../../types'
import { columns } from './constants'
import useHasPermission from '@/common/hooks/useHasPermission'
import { OperatorGroupPermission } from '@/schema'
import EditPlayerTagModal from './components/EditPlayerTagModal'
import DeletePlayerTagModal from './components/DeletePlayerTagModal'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'

interface PlayerTagsListingProps {
	data: PlayerTagResponse[]
	total: number
	isLoading: boolean
	onCreateTag?: () => void
	onEditTag?: (tag: PlayerTagResponse) => void
	onDeleteTag?: (tag: PlayerTagResponse) => void
	onRefetch?: () => void
}

export default function PlayerTagsListing({
	data,
	total,
	isLoading,
	onCreateTag,
	onEditTag,
	onDeleteTag,
	onRefetch,
}: PlayerTagsListingProps) {
	const hasWritePermission = useHasPermission(
		OperatorGroupPermission.MANAGE_PLAYER_TAGS,
	)

	const { data: presetsData } = useQueryGetPresetId()

	const [editModalOpen, setEditModalOpen] = useState(false)
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [selectedTagId, setSelectedTagId] = useState<string | null>(null)
	const [selectedTag, setSelectedTag] = useState<PlayerTagResponse | null>(null)

	const handleEdit = (tag: PlayerTagResponse) => {
		setSelectedTagId(tag.id)
		setEditModalOpen(true)
		onEditTag?.(tag)
	}

	const handleDelete = (tag: PlayerTagResponse) => {
		setSelectedTag(tag)
		setDeleteModalOpen(true)
		onDeleteTag?.(tag)
	}

	// Pass handlers to columns via context or create columns here
	const columnsWithActions = columns(
		hasWritePermission || false,
		handleEdit,
		handleDelete,
		presetsData,
	)

	return (
		<>
			<div className="space-y-4">
				{/* Header with create button */}
				<div className="flex items-start justify-between">
					<Heading
						title="จัดการแท็กผู้เล่น"
						description="จัดการแท็กสำหรับผู้เล่น"
					/>
					<div className="flex gap-2">
						<Button variant="outline" onClick={onRefetch} disabled={isLoading}>
							<RefreshCw className="mr-2 h-4 w-4" />
							รีเฟรช
						</Button>
						{hasWritePermission && (
							<Button onClick={onCreateTag}>
								<Plus className="mr-2 h-4 w-4" />
								เพิ่มแท็กใหม่
							</Button>
						)}
					</div>
				</div>
				<Separator />

				{/* Search */}
				<div className="flex flex-wrap items-end gap-4">
					<DataTableSimpleSearch placeHolder="ค้นหาชื่อแท็ก" />
				</div>

				{/* Table */}
				<DataTablePaginate
					columns={columnsWithActions}
					data={data}
					totalItems={total}
					isLoading={isLoading}
				/>
			</div>

			{/* Edit Modal */}
			<EditPlayerTagModal
				open={editModalOpen}
				onOpenChange={setEditModalOpen}
				tagId={selectedTagId}
			/>

			{/* Delete Modal */}
			{selectedTag && (
				<DeletePlayerTagModal
					open={deleteModalOpen}
					onOpenChange={setDeleteModalOpen}
					tag={selectedTag}
				/>
			)}
		</>
	)
}
